// src/pages/userprofile/UserprofileListPage.tsx
// Profiles list with all fields.

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useUserprofile, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardContent, ErrorAlert} from '@react/components';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { StatusBadge } from '../../components/StatusBadge';
import { formatProfileStatus } from '../../features/userprofile/logic';
import type { Userprofile } from '@core/types/userprofile';

export const UserprofileListPage = observer(() => {
  const profileStore = useUserprofile();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');

  useEffect(() => { profileStore.ListUserprofiles({ limit: 20 } as never); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { q: search } : {});
    profileStore.ListUserprofiles({ limit: 20, search } as never);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('userprofile.deleteConfirm'))) return;
    try { await profileStore.DeleteUserprofile(id); } catch { /* store handles */ }
  };

  const profiles = profileStore.items as Userprofile[];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('userprofile.list')}</h1>
        <Link to="/userprofiles/new" className="ml-auto"><Button>{t('userprofile.create')}</Button></Link>
      </div>

        {profileStore.error ? <ErrorAlert error={profileStore.error} dismissible onDismiss={() => profileStore.clearError()} /> : null}

      <Card>
        <CardHeader>
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardHeader>
        <CardContent>
          <Table hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('userprofile.displayName')}</TableHead>
                <TableHead>{t('userprofile.firstName')}</TableHead>
                <TableHead>{t('userprofile.lastName')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead>{t('userprofile.isDefault')}</TableHead>
                <TableHead>{t('userprofile.location')}</TableHead>
                <TableHead>{t('userprofile.language')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.length === 0 && !profileStore.isLoading && <TableEmpty colSpan={8} message={t('userprofile.noItems')} />}
              {profiles.map((p) => {
                const status = formatProfileStatus(p.status ?? '');
                return (
                  <TableRow key={p.entity?.id}>
                    <TableCell>
                      <Link to={`/userprofiles/${p.entity?.id}`} className="text-sm font-medium text-primary hover:text-primary/80">
                        {p.display_name || `${p.first_name} ${p.last_name}`}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm">{p.first_name}</TableCell>
                    <TableCell className="text-sm">{p.last_name}</TableCell>
                    <TableCell><StatusBadge variant={status.variant} label={t(status.labelKey)} size="sm" /></TableCell>
                    <TableCell><span className={p.is_default ? 'text-green-600' : 'text-muted-foreground'}>{p.is_default ? t('common.yes') : t('common.no')}</span></TableCell>
                    <TableCell className="text-sm">{p.location ?? '-'}</TableCell>
                    <TableCell className="text-sm">{p.language ?? '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Link to={`/userprofiles/${p.entity?.id}`}><Button variant="ghost" size="sm">{t('common.open')}</Button></Link>
                        <Link to={`/userprofiles/${p.entity?.id}/edit`}><Button variant="ghost" size="sm">{t('common.edit')}</Button></Link>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(p.entity?.id ?? '')}>{t('common.delete')}</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {profileStore.hasMore && (
            <div className="mt-4 text-center">
              <Button variant="secondary" size="sm" onClick={() => profileStore.ListUserprofiles({ limit: 20, cursor: profileStore.pageInfo?.next_cursor } as never)} isLoading={profileStore.isLoading}>{t('common.next')}</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default UserprofileListPage;
